import React from "react";
import { Form, Row, Col, Button, Input, Select, Popover, Modal, Slider, Switch } from "antd";
import { HeartFilled, HeartOutlined, ControlOutlined, FilterOutlined, DownloadOutlined, InfoCircleOutlined, ThunderboltFilled } from "@ant-design/icons";
import LiquidGlass from "./LiquidGlass";
import FileImporter from "./FileImporter";

const { Option } = Select;

function categoryLabelWithStats({ plansInfo, category }) {
  const catKey = String(category || "").trim();
  if (!catKey) return "";

  const plans = (plansInfo || []).filter((p) => String(p?.category || "").trim() === catKey);
  const x = plans.length;
  if (x <= 0) return catKey;

  const weekRe = /week\s*([0-9]{1,3})/i;
  let weekMentionCount = 0;
  let maxWeek = null;

  plans.forEach((p) => {
    const name = String(p?.name || "").trim() || String(p?.display_name || "").trim();
    if (!name) return;
    if (/week/i.test(name)) weekMentionCount += 1;
    const m = name.match(weekRe);
    if (m && m[1] != null) {
      const n = Number(m[1]);
      if (!Number.isNaN(n)) maxWeek = maxWeek === null ? n : Math.max(maxWeek, n);
    }
  });

  const hasWeeks = weekMentionCount >= Math.ceil(x / 2) && maxWeek !== null;
  const plansLabel = `${x} plan${x === 1 ? "" : "s"}`;
  const weeksLabel = `${maxWeek} week${maxWeek === 1 ? "" : "s"}`;
  const suffix = hasWeeks ? `(${weeksLabel}, ${plansLabel})` : `(${plansLabel})`;
  return `${catKey} ${suffix}`;
}

function PlanSetup({
  // actions
  saveTrainingPlan,
  getAllTrainingPlansInfo,
  getPlanLabelByUuid,
  showModal,
  handleOk,
  handleCancel,
  selectPlan,
  setFavourite,
  onAdjustPower,
  onShowTrainingForm, // NEW: open the training form
  goToLetsRide, // NEW: navigate to "Let's Ride"
  showTrainingForm, // NEW: controls visibility of advanced editor block
  isPremium,
  onRequestPremium,
  lastAiPlanBuilder = null,
  hasOngoingAiPlanBuilder = false,
  lastAiPlanBuilderSingle = null,
  hasOngoingAiPlanBuilderSingle = false,
  setOtherUserInfo = () => {},
  onOpenAiPlanBuilderProgram,
  onResumeAiPlanBuilderProgram,
  onOpenAiSingleSessionBuilder,
  onResumeAiSingleSessionBuilder,
  persistAiBuilderState,
  persistAiSingleBuilderState,
  // state + setters
  FTP,
  setFTP,
  steps,
  planName,
  handlePlanNameChange,
  planCategory,
  setPlanCategory,
  planDescription,
  setPlanDescription,
  previousUuid,
  setPreviousUuid,
  setPlanName,
  setSteps,
  createWarmUpSteps,
  isMobile,
  setUsernameToFetch,
  isModificationMade,
  selectedPlan,
  plansInfo,
  username,
  isModalOpen,
  planIsFavourite,
  planStressPoints,
  setPlanStressPoints,
  refFTPForAdjust,
  setRefFTPForAdjust,
  lastNon100FTP,
  arePlansLoading,
  // UI
  siderCollapsed = true,
}) {
  // no dialog needed for SimplyRide navigation
  const isEditing = !!showTrainingForm;
  const [fetchInputKey, setFetchInputKey] = React.useState(0);
  const [showFilterRow, setShowFilterRow] = React.useState(false);
  // Premium gating removed: AI Coach is available to all users (credits enforced inside AI builder).

  const openAiBuilder = async () => {
    try {
      const showResumeOverwriteOrClose = ({ title, content, onResume, onOverwrite }) => {
        let instance;
        const handleOverwrite = () => {
          try {
            onOverwrite && onOverwrite();
          } catch {}
          try {
            instance && instance.destroy();
          } catch {}
        };
        instance = Modal.confirm({
          title,
          content,
          okText: "Resume",
          closable: true,
          maskClosable: true,
          onOk: () => {
            try {
              onResume && onResume();
            } catch {}
          },
          onCancel: () => {
            // Close only (X / ESC / outside-click should not be treated as "Overwrite")
          },
          footer: (_, { OkBtn, CancelBtn }) => (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <Button danger onClick={handleOverwrite}>
                Overwrite
              </Button>
              <OkBtn />
            </div>
          ),
        });
      };

      const handleChooseProgram = () => {
        const saved = lastAiPlanBuilder && typeof lastAiPlanBuilder === "object" ? lastAiPlanBuilder : null;
        const isOngoing = Boolean(hasOngoingAiPlanBuilder) && saved && (saved.status || "in_progress") === "in_progress";
        if (isOngoing) {
          showResumeOverwriteOrClose({
            title: "Resume your Simpl'AI Program Builder?",
            content: "We found an unfinished whole-program builder. Resume, or overwrite it and start fresh?",
            onResume: () => {
              try {
                if (typeof onResumeAiPlanBuilderProgram === "function") onResumeAiPlanBuilderProgram(saved);
              } catch {}
            },
            onOverwrite: () => {
              try {
                if (typeof onOpenAiPlanBuilderProgram === "function") onOpenAiPlanBuilderProgram({ clearExistingDraft: true });
              } catch {}
            },
          });
          return;
        }
        try {
          if (typeof onOpenAiPlanBuilderProgram === "function") onOpenAiPlanBuilderProgram({ clearExistingDraft: false });
        } catch {}
      };

      const handleChooseSingle = () => {
        const saved = lastAiPlanBuilderSingle && typeof lastAiPlanBuilderSingle === "object" ? lastAiPlanBuilderSingle : null;
        const isOngoing = Boolean(hasOngoingAiPlanBuilderSingle) && saved && (saved.status || "in_progress") === "in_progress";
        if (isOngoing) {
          showResumeOverwriteOrClose({
            title: "Resume your Simpl'AI Single Workout Builder?",
            content: "We found an unfinished single-workout builder. Resume, or overwrite it and start fresh?",
            onResume: () => {
              try {
                if (typeof onResumeAiSingleSessionBuilder === "function") onResumeAiSingleSessionBuilder(saved);
              } catch {}
            },
            onOverwrite: () => {
              try {
                if (typeof onOpenAiSingleSessionBuilder === "function") onOpenAiSingleSessionBuilder({ clearExistingDraft: true });
              } catch {}
            },
          });
          return;
        }
        try {
          if (typeof onOpenAiSingleSessionBuilder === "function") onOpenAiSingleSessionBuilder({ clearExistingDraft: false });
        } catch {}
      };

      // 3-way choice modal: Whole program / Single workout / Close (X)
      let introInstance;
      const handleIntroSingleClick = () => {
        try {
          introInstance && introInstance.destroy();
        } catch {}
        handleChooseSingle();
      };
      introInstance = Modal.confirm({
        title: "Simpl'AI Coach - what do you want to create?",
        content: "Choose between creating a single workout session (one ride) or a whole program (multi-week plan).",
        okText: "Whole program",
        closable: true,
        maskClosable: true,
        onOk: handleChooseProgram,
        onCancel: () => {
          // Close only (X / ESC / outside-click should not be treated as "Single workout")
        },
        footer: (_, { OkBtn }) => (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={handleIntroSingleClick}>Single workout</Button>
            <OkBtn />
          </div>
        ),
      });
    } catch (e) {
      console.error("Failed to load AI coach intro:", e);
      // Fallback: open the dedicated AI Plan Builder page (it will show its own error state).
      try {
        if (typeof onOpenAiPlanBuilderProgram === "function") onOpenAiPlanBuilderProgram({ clearExistingDraft: false });
      } catch {}
    } finally {
      // no-op (AI Plan Builder page handles its own loading state)
    }
  };
  const allCategories = React.useMemo(() => {
    const set = new Set();
    (plansInfo || []).forEach((p) => {
      if (p && p.category) set.add(p.category);
    });
    return Array.from(set).sort();
  }, [plansInfo]);

  // "Program" categories = categories where at least half of the plan names mention "week" (case-insensitive).
  const programCategories = React.useMemo(() => {
    const counts = new Map(); // cat -> { total, week }
    (plansInfo || []).forEach((p) => {
      const cat = String(p?.category || "").trim();
      if (!cat) return;
      const cur = counts.get(cat) || { total: 0, week: 0 };
      cur.total += 1;
      const name = String(p?.name || "").trim() || String(p?.display_name || "").trim();
      if (name && /week/i.test(name)) cur.week += 1;
      counts.set(cat, cur);
    });
    return (allCategories || []).filter((c) => {
      const v = counts.get(c);
      if (!v || !v.total) return false;
      return v.week >= Math.ceil(v.total / 2);
    });
  }, [plansInfo, allCategories]);

  const [programOnly, setProgramOnly] = React.useState(false);
  const displayedCategories = React.useMemo(() => (programOnly ? programCategories : allCategories), [programOnly, programCategories, allCategories]);

  const categoryOptions = React.useMemo(() => {
    return (displayedCategories || []).map((c) => ({
      value: c,
      label: categoryLabelWithStats({ plansInfo, category: c }) || c,
    }));
  }, [displayedCategories, plansInfo]);
  const tssMinMax = React.useMemo(() => {
    const values = (plansInfo || [])
      .map((p) => (p && typeof p.stress_points === "number" ? p.stress_points : (p && p.stress_points ? Number(p.stress_points) : null)))
      .filter((v) => typeof v === "number" && !Number.isNaN(v));
    if (!values.length) return [0, 200];
    return [Math.min(...values), Math.max(...values)];
  }, [plansInfo]);
  const [tssRange, setTssRange] = React.useState(tssMinMax);
  const [selectedCategories, setSelectedCategories] = React.useState(allCategories);
  const [filtersApplied, setFiltersApplied] = React.useState(false);
  const timeMinMax = React.useMemo(() => {
    const values = (plansInfo || [])
      .map((p) => {
        if (!p || !Array.isArray(p.steps)) return null;
        const totalSec = p.steps.reduce((sum, s) => sum + (s?.duration_sec || 0), 0);
        return totalSec > 0 ? totalSec / 60 : null; // minutes
      })
      .filter((v) => typeof v === "number" && !Number.isNaN(v) && v <= 180);
    if (!values.length) return [0, 180];
    return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))];
  }, [plansInfo]);
  const [timeRange, setTimeRange] = React.useState(timeMinMax);
  React.useEffect(() => {
    setSelectedCategories(programOnly ? programCategories : allCategories);
  }, [allCategories, programCategories, programOnly]); // includes toggle + plan list changes
  React.useEffect(() => {
    setTssRange(tssMinMax);
  }, [tssMinMax]);
  React.useEffect(() => {
    setTimeRange(timeMinMax);
  }, [timeMinMax]);

  const filteredPlans = React.useMemo(() => {
    let base = plansInfo || [];
    if (!filtersApplied) return base;
    const upperOpen = Array.isArray(timeRange) && timeRange[1] >= timeMinMax[1];
    return base.filter((p) => {
      const tss = typeof p.stress_points === "number" ? p.stress_points : Number(p.stress_points || 0);
      const cat = p.category || "";
      const inCat = selectedCategories.length === 0 ? false : selectedCategories.includes(cat);
      const inTss = tss >= tssRange[0] && tss <= tssRange[1];
      const totalSec = Array.isArray(p.steps) ? p.steps.reduce((sum, s) => sum + (s?.duration_sec || 0), 0) : 0;
      const minutes = totalSec / 60;
      // Exclude durations > 180 by default (likely errors) unless user chose max (open upper bound)
      if (!upperOpen && minutes > 180) return false;
      const inTime = upperOpen ? minutes >= timeRange[0] : (minutes >= timeRange[0] && minutes <= timeRange[1]);
      return inCat && inTss && inTime;
    });
  }, [plansInfo, filtersApplied, selectedCategories, tssRange, timeRange, timeMinMax]);

  const programCount = (programCategories || []).length;
  const totalCount = (allCategories || []).length;
  const displayedCount = (displayedCategories || []).length;

  const categoriesPlaceholder = React.useMemo(() => {
    if (programOnly) return `Select programs (${displayedCount})`;
    const progLabel = `${programCount} program${programCount === 1 ? "" : "s"}`;
    return `Select categories (${totalCount}, including ${progLabel})`;
  }, [programOnly, displayedCount, totalCount, programCount]);

  const allDisplayedSelected = React.useMemo(() => {
    if (!Array.isArray(displayedCategories) || displayedCategories.length === 0) return false;
    const sel = new Set(selectedCategories || []);
    return displayedCategories.every((c) => sel.has(c));
  }, [displayedCategories, selectedCategories]);

  const userOwnedPlansForPreviousLink = React.useMemo(() => {
    const u = (username || "").toString().toLowerCase().trim();
    const cur = selectedPlan?.uuid === undefined || selectedPlan?.uuid === null ? null : String(selectedPlan.uuid);
    return (plansInfo || [])
      .filter((p) => {
        if (!p) return false;
        const pu = (p.username || "").toString().toLowerCase().trim();
        const uuid = p.uuid === undefined || p.uuid === null ? "" : String(p.uuid);
        // eslint-disable-next-line eqeqeq
        if (!uuid || uuid == "1") return false;// make it == because it can be string vs number. do not change to ===
        if (cur && uuid === cur) return false; // avoid self-link
        return pu && u && pu === u;
      })
      .sort((a, b) => String(a.display_name || a.name || "").localeCompare(String(b.display_name || b.name || "")));
  }, [plansInfo, username, selectedPlan]);

  // Previous plan display: if previousUuid points to an external plan (friend/SimplyRide),
  // keep the selectable list restricted to user-owned plans but still show a friendly label.
  const previousUuidStr = React.useMemo(() => {
    if (previousUuid === null || previousUuid === undefined) return null;
    const s = String(previousUuid).trim();
    return s ? s : null;
  }, [previousUuid]);

  const previousPlanFromAnyPlans = React.useMemo(() => {
    if (!previousUuidStr) return null;
    return (plansInfo || []).find((p) => String(p?.uuid) === previousUuidStr) || null;
  }, [plansInfo, previousUuidStr]);

  const isPreviousUuidUserOwnedOption = React.useMemo(() => {
    if (!previousUuidStr) return false;
    return (userOwnedPlansForPreviousLink || []).some((p) => String(p?.uuid) === previousUuidStr);
  }, [userOwnedPlansForPreviousLink, previousUuidStr]);

  const [externalPreviousPlanLabel, setExternalPreviousPlanLabel] = React.useState(null);

  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (!previousUuidStr || previousUuidStr == "1") {// make it == because it can be string vs number. do not change to ===
      setExternalPreviousPlanLabel(null);
      return;
    }
    // If it's already in the loaded plans list, or it's one of the user-owned selectable options,
    // we don't need an extra lookup.
    if (previousPlanFromAnyPlans || isPreviousUuidUserOwnedOption) {
      setExternalPreviousPlanLabel(null);
      return;
    }
    if (typeof getPlanLabelByUuid !== "function") {
      setExternalPreviousPlanLabel(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lbl = await getPlanLabelByUuid(previousUuidStr);
        if (!cancelled) setExternalPreviousPlanLabel(lbl || null);
      } catch {
        if (!cancelled) setExternalPreviousPlanLabel(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previousUuidStr, previousPlanFromAnyPlans, isPreviousUuidUserOwnedOption, getPlanLabelByUuid]);

  const previousPlanDisplayLabel = React.useMemo(() => {
    if (!previousUuidStr) return null;
    return (
      previousPlanFromAnyPlans?.display_name ||
      previousPlanFromAnyPlans?.name ||
      externalPreviousPlanLabel ||
      previousUuidStr
    );
  }, [previousUuidStr, previousPlanFromAnyPlans, externalPreviousPlanLabel]);

  // Quick export of current plan to .erg (available even in view mode)
  const downloadErgFromCurrentPlan = () => {
    if (!steps || steps.length === 0 || !FTP) return;
    const usePercentage = true;
    const descriptionText = planDescription ? `\nDESCRIPTION = ${planDescription}` : "";
    let ergContent = `[COURSE HEADER]
VERSION = 2
UNITS = ENGLISH
FILE NAME = ${planName || "workout"}${descriptionText}
FTP = ${FTP}
POWER MODE = ${usePercentage ? "PERCENTAGE" : "WATTS"}
MINUTES WATTS
[END COURSE HEADER]
[COURSE DATA]
`;
    let totalMinutes = 0;
    let lastPercentageOfFTP = null;
    steps.forEach((step, i) => {
      const { duration, watt } = step || {};
      if (!duration || typeof watt !== "number") return;
      if (lastPercentageOfFTP !== null) {
        ergContent += `${totalMinutes.toFixed(2)}\t${lastPercentageOfFTP.toFixed(2)}\n`;
      }
      const percentageOfFTP = (watt / FTP) * 100;
      ergContent += `${totalMinutes.toFixed(2)}\t${percentageOfFTP.toFixed(2)}\n`;
      totalMinutes += duration / 60;
      lastPercentageOfFTP = percentageOfFTP;
      if (i === steps.length - 1) {
        ergContent += `${totalMinutes.toFixed(2)}\t${percentageOfFTP.toFixed(2)}\n`;
      }
    });
    ergContent += `[END COURSE DATA]`;
    const blob = new Blob([ergContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "simplyride_app_workout.erg";
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      {/* Quick-start: Choose plan + actions */}
      <LiquidGlass className="liquid-glass" style={{ marginBottom: 12 }}>
        <Form layout="vertical">
          <Row gutter={12} align="middle">
            <Col span={11}>
              <Form.Item label={<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>Choose a plan</span>
                {!arePlansLoading && (plansInfo?.length > 0) && (
                  <FilterOutlined
                    onClick={() => setShowFilterRow((s) => !s)}
                    style={{
                      cursor: "pointer",
                      fontSize: 16,
                      color: showFilterRow ? "#ffffff" : "#1f1f1f",
                      padding: showFilterRow ? "2px 6px" : "2px 4px",
                      borderRadius: 4,
                      background: showFilterRow ? "#1677ff" : "transparent",
                      boxShadow: showFilterRow
                        ? "0 2px 0 rgba(5,145,255,.1), 0 3px 6px -4px rgba(0,0,0,.12), 0 8px 16px -8px rgba(0,0,0,.08)"
                        : "none",
                      transition: "all .2s ease",
                    }}
                    title={showFilterRow ? "Hide filters" : "Show filters"}
                  />
                )}
              </div>}>
                <Select
                  style={{ width: "100%" }}
                  popupMatchSelectWidth={false}
                  showSearch={true}
                  optionFilterProp="label"
                  onChange={(value) => {
                    selectPlan(value, plansInfo);
                  }}
                  value={selectedPlan.uuid}
                  placeholder="Choose existing plan"
                >
                  {[{ uuid: 1, display_name: arePlansLoading ? "Training sessions are loading..." : `Select training session (${(filtersApplied ? filteredPlans : plansInfo).length} sessions loaded${filtersApplied ? ", filter applied" : ""})` }, ...(filtersApplied ? filteredPlans : plansInfo)]
                    .sort((a, b) => {
                      // eslint-disable-next-line eqeqeq
                      if (a.uuid == "1") return -10; // make it == because it can be string vs number. do not change to ===
                      // eslint-disable-next-line eqeqeq
                      if (b.uuid == "1") return 10; // make it == because it can be string vs number. do not change to ===
                      if (a.display_name.includes("❤") && !b.display_name.includes("❤")) return -1;
                      if (!a.display_name.includes("❤") && b.display_name.includes("❤")) return 1;
                      if (a.username === username && b.username !== username) return -1;
                      if (a.username !== username && b.username === username) return 1;
                      return a.order < b.order ? -1 : 1;
                    })
                    .map((planInfo) => (
                      <Option key={planInfo.uuid} label={planInfo.display_name} value={planInfo.uuid}>
                        {planInfo.display_name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={1} style={{ display: "flex", justifyContent: "left" }}>
              <Popover
                content={<p>Save this plan as favourite to better find it later !</p>}
                title="Save for later ?"
              >
                <Form.Item label=" ">
                  {planIsFavourite ? (
                    <HeartFilled style={{ color: "red", fontSize: "20px" }} onClick={() => setFavourite({ favourite: false })} />
                  ) : (
                    <HeartOutlined
                      disabled={true}
                      style={{ color: "black", fontSize: "20px" }}
                      onClick={() => setFavourite({ favourite: true })}
                    />
                  )}
                </Form.Item>
              </Popover>
            </Col>
            <Col span={4}>
              <Form.Item label=" ">
                <Button
                  block
                  type="primary"
                  onClick={() => {
                    goToLetsRide && goToLetsRide();
                  }}
                >
                  SimplyRide
                </Button>
              </Form.Item>
            </Col>
            <Col
              span={1}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
            >
              <div style={{ fontWeight: 600, color: "rgba(0,0,0,0.65)" }}>OR</div>
            </Col>
            {/* New quick controls row below will host fetch/FTP/adjust/favorite */}
            <Col span={4}>
              <Form.Item label=" ">
                <Button
                  block
                  onClick={() => {
                    selectPlan(-1, plansInfo);
                    onShowTrainingForm && onShowTrainingForm();
                  }}
                >
                  Create a new Plan
                </Button>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label=" ">
                <Button
                  block
                  className="ai-coach-btn"
                  onClick={() => {
                    openAiBuilder();
                  }}
                >
                  <ThunderboltFilled className="ai-coach-icon" /> {siderCollapsed ? "Simpl'AI Coach" : "AI coach"}
                </Button>
              </Form.Item>
            </Col>
          </Row>
          {showFilterRow && (
            <Row gutter={12} align="middle" style={{ marginTop: 4 }}>
              <Col span={5}>
                <Form.Item label={`Filter by TSS (${tssRange[0]} - ${tssRange[1]})`}>
                  <Slider
                    range
                    min={tssMinMax[0]}
                    max={tssMinMax[1]}
                    value={tssRange}
                    onChange={(range) => Array.isArray(range) && setTssRange(range)}
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item label={`Filter by Duration (${timeRange[0]} - ${timeRange[1]} min)`}>
                  <Slider
                    range
                    min={timeMinMax[0]}
                    max={timeMinMax[1]}
                    value={timeRange}
                    onChange={(range) => Array.isArray(range) && setTimeRange(range)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Category / Program">
                  <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: "100%" }}
                      placeholder={categoriesPlaceholder}
                      value={selectedCategories}
                      onChange={(vals) => setSelectedCategories(vals)}
                      options={categoryOptions}
                      maxTagCount="responsive"
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: 12 }}>Program only</span>
                      <Switch size="small" checked={programOnly} onChange={(v) => setProgramOnly(!!v)} />
                    </div>
                    {allDisplayedSelected ? (
                      <Button size="small" onClick={() => setSelectedCategories([])} disabled={displayedCount === 0}>
                        Unselect all
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => setSelectedCategories(displayedCategories)} disabled={displayedCount === 0}>
                        Select all
                      </Button>
                    )}
                  </div>
                </Form.Item>
              </Col>
              <Col span={2}>
                <Form.Item label=" ">
                  <Button
                    block
                    type="primary"
                    onClick={() => setFiltersApplied(true)}
                    disabled={arePlansLoading}
                  >
                    Apply
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={12} align="middle">
            <Col span={8}>
              <Popover
                content={
                  <p>
                    - You can load existing plans from friends.
                    <br />- Simply enter their username and refresh.
                    <br />- If left blank (default), your plans, favorites, and SimplyRide&apos;s plans are loaded.
                  </p>
                }
                title="How does it work?"
              >
                <Form.Item label="Fetch plan from">
                  <Input
                    key={fetchInputKey}
                    placeholder="username"
                    onChange={(e) => setUsernameToFetch(e.target.value)}
                  />
                </Form.Item>
              </Popover>
            </Col>
            <Col span={3}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  block
                  onClick={() => {
                    // Use current usernameToFetch stored by parent via setUsernameToFetch
                    getAllTrainingPlansInfo({ uuid: 1 });
                  }}
                >
                  Fetch
                </Button>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label=" ">
                <Button
                  block
                  onClick={() => {
                    setUsernameToFetch("");
                    setFetchInputKey((k) => k + 1); // visually clear the input
                    // Reset filters as well
                    setFiltersApplied(false);
                    setShowFilterRow(false);
                    setTssRange(tssMinMax);
                    setSelectedCategories(allCategories);
                    setTimeRange(timeMinMax);
                    // Explicitly fetch defaults with empty username to avoid stale state
                    getAllTrainingPlansInfo({ uuid: 1, username_to_fetch: "" });
                  }}
                >
                  Reset
                </Button>
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label={
                  <span>
                    FTP{" "}
                    <Popover
                      content={
                        <div style={{ maxWidth: '45vw' }}>
                          <p>
                            <strong>How to test your FTP:</strong><br />
                            Search for 'FTP Tests' category training sessions to estimate your FTP. You can explore the
                            different tests by reading their descriptions. Your FTP score will be provided upon completion
                            of the test.
                          </p>
                          <p>
                            <strong>Tip:</strong> To enter power as a percentage of your FTP instead of the actual
                            value, set this field to 100. Enter the watts as a percentage of your FTP, then reset the field
                            to your actual FTP when finished. Don't forget to click "Adjust"!
                          </p>
                          <p>
                            <em>For more information about FTP: <a href="https://www.trainerroad.com/blog/what-ftp-really-means-to-cyclists/" target="_blank" rel="noopener noreferrer">What FTP Really Means to Cyclists</a></em>
                          </p>
                        </div>
                      }
                      title="FTP Information"
                      trigger="hover"
                      overlayStyle={{ maxWidth: '50vw' }}
                    >
                      <InfoCircleOutlined
                        className="ftp-info-icon"
                        style={{
                          color: '#1890ff',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                      />
                    </Popover>
                  </span>
                }
                help={
                  selectedPlan.ref_ftp
                    ? `Selected plan FTP is ${selectedPlan.ref_ftp}W. It has been adjusted to your last ref : (${refFTPForAdjust}W).`
                    : refFTPForAdjust !== FTP
                    ? `Last ref FTP : ${refFTPForAdjust}`
                    : ""
                }
              >
                <Input
                  type="number"
                  addonAfter="Watts"
                  value={FTP}
                  onChange={(e) => e.target.value >= 0 && setFTP(parseInt(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="Adjust">
                <Popover
                  content={
                    <p>
                      It allows you to adjust all power (Watts) of the session according to your FTP.
                      <br />
                      To build your plan based on FTP %, set 100 as FTP reference. Adjust to your actual FTP at the end.
                    </p>
                  }
                  title="What is that ?"
                >
                  <Button block onClick={onAdjustPower}>
                    <ControlOutlined />
                  </Button>
                </Popover>
              </Form.Item>
            </Col>
            
          </Row>
        </Form>
      </LiquidGlass>

      {/* Advanced: full plan settings */}
      {(isEditing || (selectedPlan && selectedPlan.uuid !== 1)) && (
      <LiquidGlass className="liquid-glass" style={{ marginBottom: 12 }}>
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item label=" ">
              {isEditing ? (
                <Button
                  block
                  type="primary"
                  onClick={saveTrainingPlan}
                  disabled={!FTP || steps.length === 0 || !planName}
                >
                  Save Training Plan
                </Button>
              ) : (
                <Button
                  block
                  type="primary"
                  onClick={() => {
                    if (!selectedPlan || selectedPlan.uuid === 1) {
                      return;
                    }
                    onShowTrainingForm && onShowTrainingForm();
                  }}
                  disabled={!selectedPlan || selectedPlan.uuid === 1}
                >
                  Modify
                </Button>
              )}
            </Form.Item>
          </Col>
          <Col span={isEditing ? 7 : 7}>
            <Form.Item label="Plan Name">
              <Input value={planName} onChange={handlePlanNameChange} disabled={!isEditing} />
            </Form.Item>
          </Col>

          <Col span={isEditing ? 4 : 5}>
            <Form.Item label="Category">
              <Input
                value={planCategory}
                placeholder="(Optional)"
                onChange={(e) => setPlanCategory(e.target.value)}
                disabled={!isEditing}
              />
            </Form.Item>
          </Col>
          {isEditing && (
            <Col span={4}>
              <Form.Item label=" ">
                <FileImporter
                  setPlanName={setPlanName}
                  setSteps={setSteps}
                  setFTP={setFTP}
                  setDescription={setPlanDescription}
                  FTP={FTP}
                  steps={steps}
                  planName={planName}
                  description={planDescription}
                  createWarmUpSteps={createWarmUpSteps}
                  isMobile={isMobile}
                />
              </Form.Item>
            </Col>
          )}
          {!isEditing && (
            <Col span={3}>
              <Form.Item label=" ">
                <Button block onClick={downloadErgFromCurrentPlan} disabled={!steps || steps.length === 0}>
                  <DownloadOutlined />
                </Button>
              </Form.Item>
            </Col>
          )}
          <Col span={5}>
            <Form.Item
              label={`Training Stress Score (${planStressPoints || "NA"})`}
              help={
                <a target="_blank" href="https://zwiftinsider.com/all-about-tss/" rel="noreferrer">
                  More info about TSS
                </a>
              }
            >
              <Slider
                min={0}
                max={200}
                onChange={(e) => setPlanStressPoints(e)}
                value={planStressPoints > 200 ? 200 : planStressPoints}
                disabled={!isEditing}
              />
            </Form.Item>
          </Col>

        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span>Previous plan</span>
                      <Popover
                        title="What is this for?"
                        content={
                          <div style={{ maxWidth: 420 }}>
                            Link this workout to a <b>previous</b> workout so the app can automatically suggest (or jump to)
                            the <b>next</b> session when you come back later.
                            <br />
                            <br />
                            This is especially useful when you build a whole program made of multiple sessions.
                          </div>
                        }
                      >
                        <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
                      </Popover>
                    </span>
                  }
                >
                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    optionFilterProp="label"
                    disabled={!isEditing}
                    value={previousUuid ? String(previousUuid) : "__none__"}
                    onChange={(val) => {
                      if (val === "__none__") {
                        setPreviousUuid && setPreviousUuid(null);
                        return;
                      }
                      setPreviousUuid && setPreviousUuid(val);
                    }}
                  >
                    <Option key="__none__" value="__none__" label="None">
                      None
                    </Option>
                    {previousUuidStr && !isPreviousUuidUserOwnedOption && (
                      <Option
                        key={`__external_prev__${previousUuidStr}`}
                        value={previousUuidStr}
                        label={previousPlanDisplayLabel || previousUuidStr}
                        disabled
                      >
                        {previousPlanDisplayLabel || previousUuidStr}
                      </Option>
                    )}
                    {userOwnedPlansForPreviousLink.map((p) => (
                      <Option key={String(p.uuid)} value={String(p.uuid)} label={p.display_name || p.name || String(p.uuid)}>
                        {p.display_name || p.name || String(p.uuid)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label="Description">
                  <Input.TextArea
                    block
                    value={planDescription}
                    onChange={(e) => setPlanDescription(e.target.value)}
                    placeholder="Training Description (optional)"
                    disabled={!isEditing}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </LiquidGlass>
      )}
    </>
  );
}

export default PlanSetup;

